import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table'
import {useState, FC, useEffect, useMemo} from 'react'
import {Row, Col, Pagination, Form, Spinner} from 'react-bootstrap'
import {Economy} from '../core/_models'
import {Link} from 'react-router-dom'
import {useAbility} from '@casl/react'
import {AbilityContext} from '../../../../modules/auth/core/AbilityContext'
import {showSwal} from '../../../../../ui-kit'

type EconomiesTableProps = {
  rows?: Economy[] | null
  isLoading: boolean
  onDelete: (iso3: string) => void
  subject: string
}

const confirmDialog = (title: string) => {
  return showSwal({
    title,
    icon: 'question',
    showCloseButton: true,
    showCancelButton: true,
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
  })
}

export const EconomiesTable: FC<EconomiesTableProps> = ({rows, isLoading, onDelete, subject}) => {
  const ability = useAbility(AbilityContext)
  const [listData, setListData] = useState<Economy[]>([])
  const [pagination, setPagination] = useState({pageIndex: 0, pageSize: 10})
  const [sorting, setSorting] = useState<SortingState>([])
  const [globalFilter, setGlobalFilter] = useState('')

  useEffect(() => {
    setListData(rows || [])
  }, [rows])

  const columnHelper = createColumnHelper<Economy>()

  const columns = useMemo(
    () => [
      columnHelper.accessor('iso3', {
        header: 'ISO3',
        cell: (info) => info.getValue(),
        meta: {
          headerClass: 'ps-4 text-start w-100px',
          cellClass: 'text-start w-100px',
        },
      }),
      columnHelper.accessor('iso2', {
        header: 'ISO2',
        cell: (info) => info.getValue(),
        meta: {
          headerClass: 'ps-4 text-start w-100px',
          cellClass: 'text-start w-100px',
        },
      }),
      columnHelper.accessor('name_en', {
        header: 'Name (EN)',
        cell: (info) => info.getValue(),
        meta: {
          headerClass: 'ps-4 text-start w-100px',
          cellClass: 'text-start w-100px',
        },
      }),
      columnHelper.accessor('iso3', {
        header: 'Actions',
        cell: (info) => {
          const iso3 = info.getValue()
          return (
            <div className='d-flex gap-3 justify-content-center'>
              <Link to={`/economies/${iso3}`} className='link-primary'>
                <span className='fa fa-eye'></span>
              </Link>
              {ability.can('delete', subject) && (
                <Link to='#' className='link-danger' onClick={() => onDelete(iso3)}>
                  <span className='fa fa-trash'></span>
                </Link>
              )}
            </div>
          )
        },
        meta: {
          headerClass: '',
          cellClass: 'text-center w-50px',
        },
        enableSorting: false,
      }),
    ],
    [ability, subject]
  )

  const dataTable = useReactTable({
    data: listData,
    columns,
    state: {
      pagination,
      sorting,
      globalFilter,
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  const pages = Array.from({length: dataTable.getPageCount()}, (_, i) => i + 1)

  return (
    <div style={{minHeight: '400px', position: 'relative'}}>
      {isLoading ? (
        <div className='d-flex align-items-center justify-content-center' style={{height: '200px'}}>
          <Spinner animation='border' role='status' variant='primary' />
        </div>
      ) : (
        <>
          <Row className='mb-5 justify-content-between'>
            <Col lg={5} md={5} className='d-flex justify-content-start align-items-center'>
              Showing{' '}
              {dataTable.getRowModel().rows.length > 0
                ? pagination.pageIndex * pagination.pageSize + 1
                : 0}{' '}
              to {Math.min((pagination.pageIndex + 1) * pagination.pageSize, listData.length)} of{' '}
              {listData.length} results
            </Col>
            <Col lg={4} md={4}>
              <Form.Control
                type='text'
                placeholder='Search by Name or ISO'
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
              />
            </Col>
          </Row>
          <table className='gy-3 table table-striped table-bordered table-hover'>
            <thead className='table-dark'>
              {dataTable.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th key={header.id} className='text-start'>
                      <div
                        className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getCanSort() && (
                          <span className='ms-2'>
                            {{
                              asc: <i className='fa-solid fa-sort-up'></i>,
                              desc: <i className='fa-solid fa-sort-down'></i>,
                              false: <i className='fa-solid fa-sort'></i>,
                            }[header.column.getIsSorted() as string] ?? (
                              <i className='fa-solid fa-sort'></i>
                            )}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {dataTable.getRowModel().rows.length > 0 ? (
                dataTable.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className='text-start'>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr className='align-middle'>
                  <td colSpan={99} className='text-center'>
                    No Data
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <Row className='my-3'>
            <Col md={6} className='d-flex justify-content-start align-items-center'>
              Display
              <Form.Select
                size='sm'
                className='w-80px mx-2'
                value={pagination.pageSize}
                onChange={(e) => setPagination({...pagination, pageSize: +e.target.value})}
              >
                <option value='10'>10</option>
                <option value='20'>20</option>
                <option value='50'>50</option>
              </Form.Select>
              results
            </Col>
            <Col md={6} className='d-flex justify-content-end'>
              <Pagination>
                <Pagination.Prev
                  onClick={() =>
                    setPagination({...pagination, pageIndex: pagination.pageIndex - 1})
                  }
                  disabled={pagination.pageIndex === 0}
                />
                {pages.map((page) => (
                  <Pagination.Item
                    key={page}
                    active={page === pagination.pageIndex + 1}
                    onClick={() => setPagination({...pagination, pageIndex: page - 1})}
                  >
                    {page}
                  </Pagination.Item>
                ))}
                <Pagination.Next
                  onClick={() =>
                    setPagination({...pagination, pageIndex: pagination.pageIndex + 1})
                  }
                  disabled={pagination.pageIndex >= dataTable.getPageCount() - 1}
                />
              </Pagination>
            </Col>
          </Row>
        </>
      )}
    </div>
  )
}
